/**
 * The browser's half of the Error Log (2026-10-02).
 *
 * The server writes down every fault it can see: any 5xx it sends, a missing
 * route our own client asked for, a timed job that failed. Some faults it can
 * never see, because the request never reached it or the page broke before
 * asking anything. Those are written down here, in the browser, and sent on:
 *
 *   - unreachable   - the request could not connect at all;
 *   - not_platform  - something answered that was not the platform: a proxy's
 *                     error page, an HTML page where JSON was expected;
 *   - browser       - the page itself failed: a crash in a screen, a code
 *                     chunk that would not load, a PDF / print / export /
 *                     file read that failed in the browser.
 *
 * WHAT IS NOT SENT. Anything the platform answered itself. A refusal working as
 * designed is not a fault; and a 5xx the platform sent is already in the log,
 * written by the server with its own stack, so a second copy from here would
 * count one fault twice.
 *
 * HELD UNTIL IT CAN BE SENT. Shane, 2026-10-02, choosing "Hold until signed
 * in": a fault before anyone has signed in, or while the server cannot be
 * reached - the case this exists for - is kept on the device (localStorage, so
 * a reload or the sign-out redirect does not lose it) and sent once there is
 * a connection and a signed-in session, marked as before sign-in where it was.
 * The same fault again adds to its entry rather than a new one: a screen
 * polling a dead server is one fault, not a queue full of copies.
 *
 * NEVER A FAULT OF ITS OWN. Sending uses plain fetch, not apiFetch, so a failed
 * send cannot reach the error box or come back here; it simply stays queued.
 */
import { ApiError, apiUrl } from './api';
import { getAccessToken } from './auth';
import { currentScreen } from './clientScreen';
import { AdminApiError, getPlatformAdminToken } from './platformAdminApi';

export type BrowserFaultKind = 'unreachable' | 'not_platform' | 'browser';
export type FaultApp = 'tenant' | 'admin';

export interface QueuedFault {
  key: string;
  app: FaultApp;
  kind: BrowserFaultKind;
  firstAt: string;
  lastAt: string;
  repeats: number;
  beforeSignIn: boolean;
  screen: string | null;
  action: string | null;
  method: string | null;
  path: string | null;
  status: number | null;
  code: string | null;
  message: string | null;
  errorName: string | null;
  errorMessage: string | null;
  stack: string | null;
  /** The X-Request-Id of the latest occurrence, matching the server's log lines. */
  requestId?: string | null;
}

const QUEUE_KEY = 'jeanieiq.faultQueue';
/** The oldest go first past this: a device offline for a week keeps the latest. */
export const MAX_QUEUE = 50;
/** Entries per request. */
const BATCH = 20;

/** Actions the browser does itself, where a non-API failure is the page's own. */
const BROWSER_ACTIONS = new Set(['download', 'open', 'print', 'export', 'document', 'read']);

function clip(v: string | null | undefined, max: number): string | null {
  if (v === null || v === undefined) return null;
  return v.length > max ? v.slice(0, max) : v;
}

function appNow(): FaultApp {
  return typeof window !== 'undefined' && window.location?.pathname.startsWith('/admin') ? 'admin' : 'tenant';
}

function tokenFor(app: FaultApp): string | null {
  return app === 'admin' ? getPlatformAdminToken() : getAccessToken();
}

// ---------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------

export function readFaultQueue(): QueuedFault[] {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as QueuedFault[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(q: QueuedFault[]): void {
  try {
    if (q.length === 0) window.localStorage.removeItem(QUEUE_KEY);
    else window.localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE)));
  } catch {
    /* Storage full or blocked: the fault is lost, and nothing else breaks. */
  }
}

type FaultFields = Omit<QueuedFault, 'key' | 'app' | 'firstAt' | 'lastAt' | 'repeats' | 'beforeSignIn' | 'screen'> & {
  /** Why it failed beyond the message; kept out of the merge key, as it differs every time. */
  detail?: string | null;
};

function withDetail(message: string | null, detail: string | null | undefined): string | null {
  if (!detail) return message;
  return message ? `${message} (${detail})` : detail;
}

/** Queue a fault, merging it into an identical one already waiting. */
export function reportFault(fields: FaultFields): void {
  if (typeof window === 'undefined') return;
  const app = appNow();
  const screen = currentScreen();
  const now = new Date().toISOString();
  const key = [app, fields.kind, fields.method, fields.path, fields.status, fields.code, fields.errorMessage, screen].join('|');
  const { detail, ...base } = fields;
  const errorMessage = clip(withDetail(base.errorMessage, detail), 4000);
  const q = readFaultQueue();
  const same = q.find((e) => e.key === key);
  if (same) {
    same.repeats += 1;
    same.lastAt = now;
    // The latest occurrence's id and reason, to match the latest log lines.
    if (base.requestId) same.requestId = base.requestId;
    if (detail) same.errorMessage = errorMessage;
  } else {
    q.push({
      ...base,
      key,
      app,
      screen,
      firstAt: now,
      lastAt: now,
      repeats: 1,
      beforeSignIn: !tokenFor(app),
      message: clip(fields.message, 2000),
      errorMessage,
      stack: clip(fields.stack, 12000),
    });
  }
  writeQueue(q);
  scheduleFlush(2_000);
}

// ---------------------------------------------------------------------------
// Which failures are faults
// ---------------------------------------------------------------------------

/** What a browser's fetch rejects with when it could not connect, in each browser's words. */
const FETCH_FAILED = /failed to fetch|networkerror|load failed|network request failed/i;

/** A failure of a request, rather than of the page's own code. */
function isRequestFailure(error: unknown): boolean {
  return (
    error instanceof ApiError ||
    error instanceof AdminApiError ||
    (error instanceof TypeError && FETCH_FAILED.test(error.message))
  );
}

/** One error object is one fault, however many handlers pass it along. */
const seen = new WeakSet<object>();

/**
 * Sort a failure: a fault only the browser can see, or not ours to send. See
 * the header for the rule. Exported so the rule can be tested on its own.
 */
export function faultKindFor(error: unknown, action?: string): BrowserFaultKind | null {
  if (error instanceof ApiError) {
    if (error.status === 0 || error.code === 'NETWORK_ERROR') return 'unreachable';
    if (error.reachedPlatform === false) return 'not_platform';
    return null;
  }
  if (error instanceof AdminApiError) {
    if (error.status === 0 || error.code === 'NETWORK_ERROR') return 'unreachable';
    // adminFetch keeps no envelope flag: a failure without the platform's code
    // is a reply the platform did not write.
    return error.code === null && error.status > 0 ? 'not_platform' : null;
  }
  if (error instanceof TypeError && FETCH_FAILED.test(error.message)) {
    return 'unreachable';
  }
  if (error instanceof DOMException && error.name === 'AbortError') return null;
  if (action && BROWSER_ACTIONS.has(action)) return 'browser';
  return null;
}

function errorBits(error: unknown): { errorName: string | null; errorMessage: string | null; stack: string | null } {
  if (error instanceof Error) return { errorName: error.name || null, errorMessage: error.message || null, stack: error.stack ?? null };
  if (error === undefined || error === null) return { errorName: null, errorMessage: null, stack: null };
  return { errorName: null, errorMessage: String(error), stack: null };
}

/** The verb a request reads as, in the error box's words. */
function actionWord(action: string | undefined, method: string | null): string | null {
  if (action) return action.charAt(0).toUpperCase() + action.slice(1);
  const m = (method ?? '').toUpperCase();
  if (!m) return null;
  if (m === 'GET') return 'Load';
  if (m === 'DELETE') return 'Delete';
  return 'Save';
}

/** A failed load, save or browser action: queued if it is a fault only the browser can see. */
export function reportFaultFromError(error: unknown, action?: string): void {
  if (error && typeof error === 'object') {
    if (seen.has(error)) return;
    seen.add(error);
  }
  const kind = faultKindFor(error, action);
  if (!kind) return;
  const api = error instanceof ApiError ? error : null;
  const admin = error instanceof AdminApiError ? error : null;
  const req = api?.request ?? admin?.request ?? null;
  const method = req?.method.toUpperCase() ?? null;
  // The screen's sentence says nothing of the status; the log does.
  const logged = api && api.reachedPlatform === false && api.status > 0 ? `Request failed with status ${api.status}` : null;
  reportFault({
    kind,
    action: actionWord(action, method),
    method,
    path: req?.path ?? null,
    status: api?.status ?? admin?.status ?? null,
    code: api?.code ?? admin?.code ?? null,
    message: logged ?? (error instanceof Error ? error.message : null),
    ...errorBits(error),
    ...(logged ? { errorMessage: logged } : {}),
    requestId: api?.requestId ?? admin?.trace.requestId ?? null,
    detail: api?.detail ?? admin?.trace.detail ?? null,
  });
}

/**
 * Noise every browser produces that is not ours: an extension's script, the
 * cross-origin "Script error." that carries nothing, and ResizeObserver's
 * harmless loop warning.
 */
function isNoise(message: string, stack: string | null, filename?: string): boolean {
  if (/ResizeObserver loop/i.test(message)) return true;
  if (message === 'Script error.' || message === 'Script error') return true;
  const where = `${filename ?? ''}\n${stack ?? ''}`;
  return /(chrome|moz|safari)-extension:\/\//.test(where);
}

/** A page that broke: a crash in a screen, a promise nobody caught, a chunk that would not load. */
export function reportPageCrash(error: unknown, code: 'PAGE_CRASH' | 'UNHANDLED_REJECTION' | 'CHUNK_LOAD_FAILED', filename?: string): void {
  if (error && typeof error === 'object') {
    if (seen.has(error)) return;
  }
  const bits = errorBits(error);
  if (isNoise(bits.errorMessage ?? '', bits.stack, filename)) return;
  // A request failure that surfaced here is sorted like any other request
  // failure - a refusal stays a refusal. Anything else is the page's own code
  // breaking, a TypeError included: "Cannot read properties of undefined" in a
  // promise is as much a crash as one thrown while drawing. (Found reading the
  // diff, 2026-10-02: every TypeError went to the request sorter, which drops
  // what it does not recognise, so those crashes were lost.)
  if (isRequestFailure(error)) {
    reportFaultFromError(error);
    return;
  }
  if (error instanceof DOMException && error.name === 'AbortError') return;
  if (error && typeof error === 'object') seen.add(error);
  reportFault({
    kind: 'browser',
    action: null,
    method: null,
    path: null,
    status: null,
    code,
    message: null,
    ...bits,
  });
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

/** What the server is sent: the fault, without the queue's own bookkeeping. */
function toPayload(e: QueuedFault): Omit<QueuedFault, 'key' | 'app'> {
  return {
    kind: e.kind,
    firstAt: e.firstAt,
    lastAt: e.lastAt,
    repeats: e.repeats,
    beforeSignIn: e.beforeSignIn,
    screen: e.screen,
    action: e.action,
    method: e.method,
    path: e.path,
    status: e.status,
    code: e.code,
    message: e.message,
    errorName: e.errorName,
    errorMessage: e.errorMessage,
    stack: e.stack,
    requestId: e.requestId ?? null,
  };
}

let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

function scheduleFlush(ms: number): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flushFaultQueue();
  }, ms);
}

/**
 * Send what can be sent: entries whose app has a signed-in session, while the
 * browser is online. Sent entries leave the queue; a refused batch (400, 413)
 * is dropped, because sending it again would be refused again; anything else
 * stays for the next try.
 */
export async function flushFaultQueue(): Promise<void> {
  if (sending || typeof window === 'undefined') return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  sending = true;
  try {
    for (const app of ['tenant', 'admin'] as const) {
      const token = tokenFor(app);
      if (!token) continue;
      const batch = readFaultQueue().filter((e) => e.app === app).slice(0, BATCH);
      if (batch.length === 0) continue;
      let res: Response;
      try {
        res = await fetch(apiUrl(app === 'admin' ? '/api/admin/faults' : '/api/faults'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ entries: batch.map(toPayload) }),
        });
      } catch {
        return; // Still cannot reach it. Kept; the next try will.
      }
      if (res.ok || res.status === 400 || res.status === 413 || res.status === 422) {
        const sent = new Set(batch.map((e) => e.key));
        writeQueue(readFaultQueue().filter((e) => !sent.has(e.key)));
      }
    }
  } finally {
    sending = false;
  }
  if (readFaultQueue().some((e) => tokenFor(e.app))) scheduleFlush(5_000);
}

let installed = false;

/**
 * Listen for page crashes and keep the queue moving. Once, from main.tsx.
 * React's own render crashes come through createRoot's onUncaughtError, which
 * main.tsx passes to reportPageCrash.
 */
export function installFaultReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e: ErrorEvent) => {
    reportPageCrash(e.error ?? new Error(e.message), 'PAGE_CRASH', e.filename);
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    reportPageCrash(e.reason, 'UNHANDLED_REJECTION');
  });
  // Vite's own event for a lazy chunk that would not load (a deploy replaced it).
  window.addEventListener('vite:preloadError', (e: Event) => {
    const payload = (e as Event & { payload?: unknown }).payload;
    reportPageCrash(payload instanceof Error ? payload : new Error('A part of the page could not be loaded.'), 'CHUNK_LOAD_FAILED');
  });
  window.addEventListener('online', () => void flushFaultQueue());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushFaultQueue();
  });
  // Covers signing in: the next tick after a session appears sends what waited.
  setInterval(() => {
    if (readFaultQueue().length > 0) void flushFaultQueue();
  }, 60_000);
  void flushFaultQueue();
}

/** Tests only. */
export function __resetFaultReporterForTests(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  sending = false;
  try {
    window.localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* nothing */
  }
}
